import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { tryApiGet } from "../api/client";
import { AUDIT_WINDOW_ORDER, type AuditWindow } from '../lib/definitions/analysis';
import { asOfMs, historyLabel, snapshotStampText, sortHistory, type AuditHistoryEntry, type AuditHistoryResponse } from "../lib/auditHistory";

// One History choice shared by every Audit tab that reads a snapshot: "Live" (the rolling
// result) or a past weekly snapshot. A snapshot is a 7-day result, so while one is chosen the
// window shows 7d.
interface AuditHistoryState {
  entries: AuditHistoryEntry[];
  takenAt: string | null;
  setTakenAt: (takenAt: string | null) => void;
}

const AuditHistoryContext = createContext<AuditHistoryState>({ entries: [], takenAt: null, setTakenAt: () => {} });

export function AuditHistoryProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<AuditHistoryEntry[]>([]);
  const [takenAt, setTakenAt] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    tryApiGet<AuditHistoryResponse>("/api/audit/history")
      .then((res) => !cancelled && setEntries(sortHistory(res?.entries ?? [])))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  const value = useMemo(() => ({ entries, takenAt, setTakenAt }), [entries, takenAt]);
  return <AuditHistoryContext.Provider value={value}>{children}</AuditHistoryContext.Provider>;
}

export function useAuditHistory(): AuditHistoryState {
  return useContext(AuditHistoryContext);
}

/** The instant relative times on an Audit page are measured against: the snapshot's own time while a
 *  snapshot is shown (so "Last heard" reads as it did then), otherwise now. */
export function useAuditAsOf(): number {
  const { takenAt } = useAuditHistory();
  return asOfMs(takenAt);
}

/** The History choice: Live, or a past 7-day snapshot (newest first). Hidden until one exists. */
export function AuditHistorySelect() {
  const { entries, takenAt, setTakenAt } = useAuditHistory();
  if (entries.length === 0) return null;
  return (
    <span className="audit-history-select">
      <label>
        <span className="audit-history-label">History</span>
        <select aria-label="History" value={takenAt ?? ""} onChange={(e) => setTakenAt(e.target.value || null)}>
          <option value="">Live</option>
          {entries.map((e) => (
            <option key={e.taken_at} value={e.taken_at}>{historyLabel(e.taken_at)}</option>
          ))}
        </select>
      </label>
      {takenAt && <span className="audit-history-stamp">{snapshotStampText(takenAt)}</span>}
      {takenAt && <button type="button" className="rf-btn rf-btn-outline" onClick={() => setTakenAt(null)}>Back to live</button>}
    </span>
  );
}

/** The window buttons (1h to 7d) and the History choice, side by side. */
export function AuditWindowControls({
  windowKey, onWindowChange,
}: { windowKey: AuditWindow; onWindowChange: (w: AuditWindow) => void }) {
  const { takenAt } = useAuditHistory();
  const shown: AuditWindow = takenAt ? "7d" : windowKey;
  return (
    <>
      <div className="theme-toggle" role="group" aria-label="Window">
        {AUDIT_WINDOW_ORDER.map((w) => (
          <button key={w} data-active={shown === w} disabled={!!takenAt && w !== "7d"} onClick={() => onWindowChange(w)}>
            {w}
          </button>
        ))}
      </div>
      <AuditHistorySelect />
    </>
  );
}
